import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import AppHeader from "@/components/navigation/AppHeader";
import AppFooter from "@/components/layout/AppFooter";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

const normalizeLegalHtml = (rawHtml) => {
  if (!rawHtml) return "";

  const parser = new DOMParser();
  const doc = parser.parseFromString(rawHtml, "text/html");
  const contentNode =
    doc.querySelector(".impressum") ||
    doc.querySelector(".entry-content") ||
    doc.body;

  let html = contentNode ? contentNode.innerHTML : rawHtml;
  html = html
    .replace(/href="\.\.\/Impressum"/gi, 'href="/impressum"')
    .replace(/href="\/Impressum"/gi, 'href="/impressum"')
    .replace(/href="\.\.\/Datenschutz"/gi, 'href="/datenschutz"')
    .replace(/href="\/Datenschutz"/gi, 'href="/datenschutz"')
    .replace(/href="\.\.\/impressum"/gi, 'href="/impressum"')
    .replace(/href="\/impressum"/gi, 'href="/impressum"')
    .replace(/href="\.\.\/datenschutz"/gi, 'href="/datenschutz"')
    .replace(/href="\/datenschutz"/gi, 'href="/datenschutz"');
  return html;
};

const LegalContentPage = ({ slug, heading }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [contentHtml, setContentHtml] = useState("");

  const endpoint = useMemo(() => `${API_URL}/api/legal/${slug}`, [slug]);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await fetch(endpoint);
        if (!response.ok) throw new Error("Dokument konnte nicht geladen werden");
        const html = await response.text();
        if (active) setContentHtml(normalizeLegalHtml(html));
      } catch (err) {
        if (active) setError(err.message || "Fehler beim Laden");
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [endpoint]);

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col" data-testid={`legal-page-${slug}`}>
      <AppHeader
        homePath="/"
        logoClassName="w-8 h-8"
        brandClassName="font-heading font-semibold text-lg sm:text-xl text-white whitespace-nowrap"
        containerClassName="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-3"
        actions={(
          <div className="flex items-center gap-2 sm:gap-4">
            <Link to="/login">
              <Button
                variant="ghost"
                className="text-gray-300 hover:text-white hover:bg-white/10 px-2.5 sm:px-4"
              >
                Anmelden
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-[#17c671] hover:bg-[#14a85d] text-white font-semibold px-2.5 sm:px-4">
                <span className="sm:hidden">Starten</span>
                <span className="hidden sm:inline">Kostenlos starten</span>
              </Button>
            </Link>
          </div>
        )}
      />

      <main className="pt-12 pb-16 px-6 lg:px-8 flex-1">
        <div className="max-w-5xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#17c671] hover:underline mb-6">
            <ArrowLeft className="w-4 h-4" />
            Zur Startseite
          </Link>
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 md:p-10">
            <h1 className="font-heading text-3xl md:text-4xl font-semibold text-[#212529] mb-8">{heading}</h1>
            {loading ? <p className="text-gray-500">Lade Inhalt...</p> : null}
            {!loading && error ? <p className="text-red-600">{error}</p> : null}
            {!loading && !error ? (
              <div className="legal-content text-[#334155]" dangerouslySetInnerHTML={{ __html: contentHtml }} />
            ) : null}
          </div>
        </div>
      </main>

      <AppFooter />
    </div>
  );
};

export default LegalContentPage;

